import { useState, useEffect } from 'react'
import { isAppShell } from '../util'

const DISMISSED_KEY = 'pwa_install_dismissed'
const DISMISSED_TTL_DAYS = 30

interface InstallPromptState {
  canInstall: boolean
  isIos: boolean
  isAndroid: boolean
  isStandalone: boolean
  isDismissed: boolean
  promptInstall: () => Promise<void>
  dismiss: () => void
}

function isDismissed(): boolean {
  const raw = localStorage.getItem(DISMISSED_KEY)
  if (!raw) return false
  try {
    const { timestamp } = JSON.parse(raw)
    const expiry = timestamp + DISMISSED_TTL_DAYS * 24 * 60 * 60 * 1000
    return Date.now() < expiry
  } catch {
    return false
  }
}

export default function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [dismissed, setDismissed] = useState<boolean>(isDismissed)

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true

  const ua = window.navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream
  const isAndroid = /Android/.test(ua)

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const promptInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
  }

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify({ timestamp: Date.now() }))
    setDismissed(true)
  }

  const canInstall = !isStandalone && !isAppShell() && !dismissed && (
    (isAndroid && deferredPrompt !== null) ||
    (isIos && !deferredPrompt)
  )

  return {
    canInstall,
    isIos,
    isAndroid,
    isStandalone,
    isDismissed: dismissed,
    promptInstall,
    dismiss,
  }
}
